import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { LogOut, Menu, ExternalLink } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const LOGO = "/tytocare-logo.png";

const ROLE_LABEL = { admin: "Amministrazione", doctor: "Area medico", nurse: "Area infermiere" };

function NavItems({ nav, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="portal-nav">
      {nav.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-medium transition-colors ${
                isActive ? "bg-white/10 text-white" : "text-white/65 hover:bg-white/5 hover:text-white"
              }`
            }
            data-testid={`portal-nav-${item.testid}`}
          >
            {({ isActive }) => (
              <>
                <span className={`absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-[#D4F84A] transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
                <Icon className="h-4.5 w-4.5 shrink-0" style={{ width: 18, height: 18 }} />
                {item.label}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function UserBox({ user, onLogout }) {
  return (
    <div className="rounded-2xl bg-white/5 p-4">
      <p className="truncate text-sm font-semibold text-white" data-testid="portal-user-name">
        {user.first_name} {user.last_name}
      </p>
      <p className="truncate text-xs text-white/55">{user.email}</p>
      <button onClick={onLogout} className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#D4F84A] hover:text-white transition-colors" data-testid="portal-logout-btn">
        <LogOut className="h-3.5 w-3.5" /> Esci
      </button>
    </div>
  );
}

export default function PortalLayout({ nav, title }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    document.title = `${title} · TytoCare Italia`;
    window.scrollTo(0, 0);
  }, [pathname, title]);

  return (
    <div className="min-h-screen bg-[#F6F4FA] text-[#1F152E] lg:flex" data-testid="portal-layout">
      <aside className="hidden lg:flex lg:w-72 lg:shrink-0 flex-col justify-between bg-[#1F152E] px-5 py-7 lg:sticky lg:top-0 lg:h-screen" data-testid="portal-sidebar">
        <div>
          <Link to="/" className="inline-flex items-center rounded-2xl bg-white px-3 py-2" data-testid="portal-logo-link">
            <img src={LOGO} alt="TytoCare" className="h-6 w-auto" />
          </Link>
          <p className="mt-6 px-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">{ROLE_LABEL[user.role]}</p>
          <div className="mt-3">
            <NavItems nav={nav} />
          </div>
        </div>
        <div className="space-y-3">
          <Link to="/" className="flex items-center gap-2 px-2 text-xs font-medium text-white/50 hover:text-white transition-colors" data-testid="portal-back-to-site">
            <ExternalLink className="h-3.5 w-3.5" /> Vai al sito pubblico
          </Link>
          <UserBox user={user} onLogout={logout} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-[#5B2C91]/10 bg-white/85 px-4 backdrop-blur-xl lg:hidden" data-testid="portal-mobile-header">
          <Link to="/" className="flex items-center">
            <img src={LOGO} alt="TytoCare" className="h-6 w-auto" />
          </Link>
          <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#5B2C91]">{ROLE_LABEL[user.role]}</span>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button aria-label="Apri menu" className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#5B2C91]/15 text-[#5B2C91]" data-testid="portal-mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[84%] bg-[#1F152E] border-r-0 text-white sm:max-w-sm">
              <div className="mt-2 inline-flex items-center rounded-2xl bg-white px-3 py-2">
                <img src={LOGO} alt="TytoCare" className="h-6 w-auto" />
              </div>
              <p className="mt-6 px-2 text-[11px] font-bold uppercase tracking-[0.2em] text-white/40">{ROLE_LABEL[user.role]}</p>
              <div className="mt-3">
                <NavItems nav={nav} onNavigate={() => setOpen(false)} />
              </div>
              <div className="mt-8">
                <UserBox user={user} onLogout={logout} />
              </div>
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-10" data-testid="portal-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
